import { useEffect, useState, Fragment } from 'react';
import { Dialog, Transition, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { XMarkIcon, FolderIcon } from '@heroicons/react/24/outline';
import axios from '../../../axios';
import NoPermissions from '../../NoPermissions/NoPermissions';
import usePermisos from '../../../hooks/usePermisos';

const PERMISOS_REQUERIDOS = [
    'categories_create',
    'categories_read',
    'categories_update'
];

const NewCatModal = ({ isOpen, onClose, categoriaSeleccionada, setCategoriaSeleccionada, catServicios, setCatServicios, catServiciosRaw, setCatServiciosRaw }) => {
    const { permisos, loading: loadingPermisos, tienePermiso } = usePermisos(PERMISOS_REQUERIDOS);
    const [categoria, setCategoria] = useState({ nombre_categoria: '', description: '' });
    const [validate, setValidate] = useState({ nombre_categoria: true });
    const [loading, setLoading] = useState(false);

    // Cargar categoría cuando se selecciona para editar
    useEffect(() => {
        if (categoriaSeleccionada?.id) {
            setValidate({ nombre_categoria: true });
            axios.get(`api/categories/${categoriaSeleccionada.id}`)
                .then((res) => {
                    setCategoria(res.data);
                })
                .catch(e => console.log(e));
        } else {
            setCategoria({ nombre_categoria: '', description: '' });
        }
    }, [categoriaSeleccionada?.id])

    const resetForm = () => {
        setCategoria({ nombre_categoria: '', description: '' });
        setValidate({ nombre_categoria: true });
    };

    const handleClose = () => {
        resetForm();
        if (categoriaSeleccionada?.id) {
            setCategoriaSeleccionada({});
        }
        onClose();
    };

    const _saveCat = (e) => {
        e.preventDefault();
        
        if (!tienePermiso('categories_create') && !tienePermiso('categories_update')) return;
        
        setLoading(true);
        const url = categoria.id ? `api/category/${categoria.id}` : 'api/categories';
        
        axios.post(url, categoria)
            .then((resp) => {
                if (resp.status === 200) {
                    if (categoria.id) {
                        setCatServicios(catServicios.map((cat) => {
                            if (resp.data.id === cat.id) {
                                return resp.data;
                            }
                            return cat;
                        }));
                        setCatServiciosRaw(catServiciosRaw.map((cat) => {
                            if (resp.data.id === cat.id) {
                                return resp.data;
                            }
                            return cat;
                        }));
                    }
                } else if (resp.status === 201) {
                    setCatServicios([...catServicios, resp.data]);
                    setCatServiciosRaw([...catServiciosRaw, resp.data]);
                }
                
                alert('Categoría guardada correctamente');
                setTimeout(() => {
                    handleClose();
                }, 500);
            })
            .catch((e) => {
                console.log("Error: ", e);
                if (e.response?.status === 422) {
                    setValidate({ nombre_categoria: false });
                    const message = e.response.data.errors.nombre_categoria?.[0] || "Error al guardar la categoría";
                    alert(message);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }


    if (loadingPermisos) {
        return (
            <Transition show={isOpen} as={Fragment}>
                <Dialog onClose={handleClose} className="fixed z-50 inset-0 overflow-y-auto rounded-lg">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <DialogBackdrop className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                        <DialogPanel className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-auto z-50 relative">
                            <div className="h-48 flex items-center justify-center">
                                <span className="text-gray-500">Cargando...</span>
                            </div>
                        </DialogPanel>
                    </div>
                </Dialog>
            </Transition>
        );
    }

    if (!tienePermiso('categories_read') && !tienePermiso('categories_create')) {
        return (
            <Transition show={isOpen} as={Fragment}>
                <Dialog onClose={handleClose} className="fixed z-50 inset-0 overflow-y-auto rounded-lg">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <DialogBackdrop className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                        <DialogPanel className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-auto z-50 relative">
                            <NoPermissions />
                        </DialogPanel>
                    </div>
                </Dialog>
            </Transition>
        );
    }

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog onClose={handleClose} className="fixed z-50 inset-0 overflow-y-auto rounded-lg">
                <div className="flex items-center justify-center min-h-screen px-4">
                    <DialogBackdrop className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                    <DialogPanel className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-auto z-50 relative">
                        {/* Header con gradiente azul */}
                        <div className="flex justify-between items-center border-b bg-linear-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <FolderIcon className="h-6 w-6 text-white" />
                                <h5 className="text-base font-semibold text-white">
                                    {categoriaSeleccionada?.id ? 'Editar Categoría' : 'Nueva Categoría'}
                                </h5>
                            </div>
                            <button onClick={handleClose} className="text-white hover:text-gray-200">
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Body */}
                        <form onSubmit={_saveCat}>
                            <div className="px-6 py-4 space-y-4">
                                {/* Nombre de la categoría */}
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre de la categoría *
                                    </label>
                                    <input
                                        id="name"
                                        type="text"
                                        value={categoria.nombre_categoria}
                                        onChange={(e) => setCategoria({ ...categoria, nombre_categoria: e.target.value })}
                                        className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                            validate.nombre_categoria ? 'border-gray-300' : 'border-red-500'
                                        }`}
                                    />
                                    {!validate.nombre_categoria && (
                                        <span className="text-sm text-red-500 mt-1">El nombre de la categoría es obligatorio</span>
                                    )}
                                </div>

                                {/* Descripción */}
                                <div>
                                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                                        Descripción
                                    </label>
                                    <textarea
                                        id="description"
                                        rows={4}
                                        value={categoria.description}
                                        onChange={(e) => setCategoria({ ...categoria, description: e.target.value })}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end gap-2 bg-gray-50 px-6 py-4 rounded-b-lg">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                                    onClick={handleClose}
                                    disabled={loading}
                                >
                                    Cancelar
                                </button>
                                {(tienePermiso('categories_update') || tienePermiso('categories_create')) && (
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        disabled={loading}
                                    >
                                        {loading ? 'Guardando...' : 'Guardar'}
                                    </button>
                                )}
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </Dialog>
        </Transition>
    );
};

export default NewCatModal;